import type { Implicant } from "@/lib/tool-kit/algebra/minimize";

/**
 * Karnaugh map geometry for 2–4 variables.
 *
 * Variables are split between the rows and the columns — one and one for two variables,
 * one and two for three, two and two for four — and each axis counts in Gray code
 * (00, 01, 11, 10), so neighbouring cells differ in exactly one variable. The map wraps:
 * the first and last row are neighbours, and so are the first and last column, which is
 * why a group can run off one edge and back on at the other.
 */
export interface KmapLayout {
  n: number;
  rowBits: number;
  colBits: number;
  rows: number;
  cols: number;
  /** Variable values on each row and column, in Gray order. */
  rowValues: number[];
  colValues: number[];
}

const GRAY: Record<number, number[]> = {
  0: [0],
  1: [0, 1],
  2: [0, 1, 3, 2],
};

export function layoutFor(n: number): KmapLayout {
  const rowBits = Math.floor(n / 2);
  const colBits = n - rowBits;
  return {
    n,
    rowBits,
    colBits,
    rows: 2 ** rowBits,
    cols: 2 ** colBits,
    rowValues: GRAY[rowBits],
    colValues: GRAY[colBits],
  };
}

/** The minterm number in a cell. */
export function cellMinterm(
  layout: KmapLayout,
  row: number,
  col: number
): number {
  return layout.rowValues[row] * 2 ** layout.colBits + layout.colValues[col];
}

/** Where a minterm sits: its row and column position. */
export function cellOf(
  layout: KmapLayout,
  minterm: number
): { row: number; col: number } {
  const rowValue = Math.floor(minterm / 2 ** layout.colBits);
  const colValue = minterm % 2 ** layout.colBits;
  return {
    row: layout.rowValues.indexOf(rowValue),
    col: layout.colValues.indexOf(colValue),
  };
}

/** The binary label of a Gray-coded position: `01`, `11`. */
export function axisLabel(value: number, bits: number): string {
  return bits === 0 ? "" : value.toString(2).padStart(bits, "0");
}

export interface Span {
  start: number;
  length: number;
}

/** The cyclic interval of positions a set of positions occupies on an axis of `size`. */
function cyclicSpan(positions: number[], size: number): Span {
  const sorted = [...new Set(positions)].sort((a, b) => a - b);
  if (sorted.length === size) {
    return { start: 0, length: size };
  }
  const present = new Set(sorted);
  for (const p of sorted) {
    if (!present.has((p - 1 + size) % size)) {
      return { start: p, length: sorted.length };
    }
  }
  return { start: sorted[0], length: sorted.length };
}

export interface GroupBox {
  rows: Span;
  cols: Span;
}

/** The block of cells an implicant covers, as cyclic row and column spans. */
export function groupBox(layout: KmapLayout, implicant: Implicant): GroupBox {
  const cells = implicant.terms.map((m) => cellOf(layout, m));
  return {
    rows: cyclicSpan(
      cells.map((c) => c.row),
      layout.rows
    ),
    cols: cyclicSpan(
      cells.map((c) => c.col),
      layout.cols
    ),
  };
}

export interface Segment {
  row: number;
  col: number;
  rowLength: number;
  colLength: number;
}

function linearSegments(span: Span, size: number): [number, number][] {
  const end = span.start + span.length;
  if (end <= size) {
    return [[span.start, span.length]];
  }
  return [
    [span.start, size - span.start],
    [0, end - size],
  ];
}

/** Plain rectangles to draw for a group: two or four when it wraps around an edge. */
export function groupSegments(layout: KmapLayout, box: GroupBox): Segment[] {
  const segments: Segment[] = [];
  for (const [row, rowLength] of linearSegments(box.rows, layout.rows)) {
    for (const [col, colLength] of linearSegments(box.cols, layout.cols)) {
      segments.push({ row, col, rowLength, colLength });
    }
  }
  return segments;
}
